import {
  Box,
  Button,
  Flex,
  Image,
  Spinner,
  Text,
  VStack,
  useToken,
} from "@chakra-ui/react";
import { GradientDivider } from "./componentsV2/GradientDivider";
import { CloseIcon } from "@chakra-ui/icons";
import { memo, useEffect, useRef } from "react";
import { FunnelComponentProps, FunnelContextArea } from "../funnel/types";
import { FUNNEL_TYPES } from "../funnel/utils/contants";
import { EventTypes } from "../../screens/root/api/eventsApi";
import { useIsVisible } from "../../hooks/useIsVisible";
import { useUserData } from "../../hooks/useUserData";
import { usePromoCode } from "./hooks/usePromoCode";
import { useSubscriptionPricingV2 } from "./hooks/useSubscriptionPricingV2";
import { FeatureValueProps } from "./componentsV2/FeatureValueProps";
import { SubscriptionNote } from "./componentsV2/SubscriptionNote";
import { ValueProps } from "./componentsV2/ValueProps";
import { Testimonials } from "./componentsV2/Testimonials";
import { SubscriptionSelector } from "./componentsV2/SubscriptionSelector";
import { StripePayment, STRIPE_FORM_ID } from "./components/StripePayment";
import { StickyPurchaseButton } from "./componentsV2/StickyPurchaseButton";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { useSubscriptionPurchaseV2 } from "./hooks/useSubscriptionPurchaseV2";
import { PromoCodeInput } from "./components/PromoCodeInput";
import { Eyebrow } from "./componentsV2/Eyebrow";
import { CaretLeftLargeIcon } from "../icons/CaretLeftLargeIcon";
import { interpretColorTags } from "../../utils/processPaywallText";
import { FunnelSteps } from "../funnel/FunnelSteps";
import { usePaywallType } from "../../hooks/usePaywallType";

export const SubscriptionModalV2 = memo(
  ({
    onClose,
    modalContext = FunnelContextArea.default,
    onSuccess: onFunnelSuccess,
    sendEvent,
    setFunnelType,
    release,
    shouldShowSteps,
    stepCount,
    currentStep,
    stepText,
    stepDisplayText,
    showStepIndicators,
    initialPromoCode,
    triggerFocusPayment = 0,
    hidePromoCode = false,
    isPaywallHeader = false,
  }: FunnelComponentProps & {
    initialPromoCode?: string;
    triggerFocusPayment?: number;
    hidePromoCode?: boolean;
    isPaywallHeader?: boolean;
  }) => {
    const isSmall = useIsSmallBreakpoint();
    const { userData } = useUserData();
    const [blazeColor] = useToken("colors", ["blaze.blaze"]);

    const isGoldUser = userData?.gold ?? false;
    const isContextWithNoX = modalContext === FunnelContextArea.releaseDetails;
    const isContextWithPrePaywall =
      modalContext === FunnelContextArea.reader ||
      modalContext === FunnelContextArea.verticalReader;
    const paymentType = usePaywallType(release);
    const hasBackButton =
      isContextWithPrePaywall &&
      !isGoldUser &&
      paymentType === FUNNEL_TYPES.prePaywall;

    const {
      promoCode,
      appliedPromoCode,
      isPromoValid,
      promoValidationError,
      setPromoCode,
      setAppliedPromoCode,
    } = usePromoCode(initialPromoCode);

    const { yearlySub, monthlySub, activeType, setActiveType, paywall, data } =
      useSubscriptionPricingV2(appliedPromoCode);

    const { submitPurchase, waiting, failedCard, cardValidationError } =
      useSubscriptionPurchaseV2({
        activeType,
        yearlySub,
        monthlySub,
        promoCode,
        onFunnelSuccess: onFunnelSuccess,
      });

    const ref = useRef<HTMLDivElement>(null);
    const isVisible = useIsVisible(ref, 0.5);
    const alreadyTracked = useRef(false);

    useEffect(() => {
      if (sendEvent && isVisible && !alreadyTracked.current) {
        sendEvent(EventTypes.viewed_subscription_offer);
        alreadyTracked.current = true;
      }
    }, [sendEvent, isVisible]);

    const mainTitle = interpretColorTags(paywall?.main_title ?? "", blazeColor);

    if (!data || !yearlySub || !monthlySub || !paywall) {
      return (
        <Flex
          width="100%"
          justifyContent="center"
          alignItems="center"
          height="531px"
          padding="24px"
          ref={ref}
        >
          <Spinner color="blaze.blaze" />
        </Flex>
      );
    }

    const activeSubOffer =
      activeType === yearlySub.billing_frequency ? yearlySub : monthlySub;

    return (
      <Box ref={ref} position="relative" width="100%">
        {paywall.background_image && (
          <Image
            src={paywall.background_image}
            position="absolute"
            top={0}
            left={0}
            width="100%"
            pointerEvents="none"
            zIndex={0}
            sx={{
              maskImage:
                "linear-gradient(to bottom, black 10%, transparent 80%)",
              WebkitMaskImage:
                "linear-gradient(to bottom, black 10%, transparent 80%)",
            }}
          />
        )}
        <Flex
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          padding="24px 24px 0"
          position="sticky"
          top={0}
          zIndex={2}
        >
          <Box>
            {hasBackButton && (
              <Button
                variant="iconOnly"
                onClick={() => setFunnelType(FUNNEL_TYPES.prePaywall)}
                minWidth={0}
                backdropFilter="blur(12px)"
                background="transparent.black.40"
                borderRadius="full"
                boxSize="40px"
              >
                <CaretLeftLargeIcon color="dune.400" boxSize="20px" />
              </Button>
            )}
          </Box>
          {isContextWithNoX ? null : (
            <Button
              gc-testing-id="btnCloseSubscriptionModal"
              variant="iconOnly"
              onClick={onClose}
              minWidth={0}
              backdropFilter="blur(12px)"
              background="transparent.black.40"
              borderRadius="full"
              boxSize="40px"
            >
              <CloseIcon color="dune.100" />
            </Button>
          )}
        </Flex>
        <VStack
          gap={6}
          py={6}
          alignItems="center"
          width="100%"
          position="relative"
          zIndex={1}
        >
          <VStack gap={6} px={6} alignItems="center" width="100%">
            <VStack alignItems="center" width="100%" mb={2}>
              <Eyebrow
                text={paywall.eyebrow}
                imageUrl={paywall.eyebrow_image_url}
              />
              {shouldShowSteps ? (
                <FunnelSteps
                  stepCount={stepCount}
                  currentStep={currentStep}
                  stepText={stepText}
                  text={stepDisplayText}
                  hideProgressIndicators={!showStepIndicators}
                />
              ) : (
                <Text
                  mt="16px"
                  variant="paymentTitle"
                  {...(isPaywallHeader && {
                    fontSize: "32px",
                    fontWeight: "600",
                  })}
                  dangerouslySetInnerHTML={{ __html: mainTitle }}
                />
              )}
            </VStack>
          </VStack>
          <VStack gap={6} px={6} alignItems="center" width="100%">
            <FeatureValueProps valueProps={paywall.feature_value_props} />
            <SubscriptionSelector
              subOffer={yearlySub}
              isActive={activeType === yearlySub.billing_frequency}
              onClick={() => setActiveType(yearlySub.billing_frequency)}
              borderColor="blaze.800"
              activeBorderColor="blaze.blaze"
              creatorLogos={paywall.creator_logos}
              creatorsCount={paywall.creators_count}
            />
            <SubscriptionSelector
              subOffer={monthlySub}
              isActive={activeType === monthlySub.billing_frequency}
              onClick={() => setActiveType(monthlySub.billing_frequency)}
              borderColor="transparent.white.20"
              activeBorderColor="transparent.white.80"
              creatorLogos={paywall.creator_logos}
              creatorsCount={paywall.creators_count}
            />
            <GradientDivider />
            <StripePayment
              formId={STRIPE_FORM_ID}
              triggerFocusPayment={triggerFocusPayment}
              waiting={waiting}
              responseError={promoValidationError ?? cardValidationError}
              failedCard={failedCard}
              completePurchase={submitPurchase}
              isSmall={isSmall}
            />
            {hidePromoCode ? null : (
              <PromoCodeInput
                isSmall={isSmall}
                promoApplied={appliedPromoCode}
                isValid={
                  appliedPromoCode === yearlySub?.promo_code ||
                  appliedPromoCode === monthlySub?.promo_code
                }
                applyPromoCode={setAppliedPromoCode}
                onChange={setPromoCode}
              />
            )}
            <GradientDivider />
            <SubscriptionNote subscriptionNote={paywall.subscription_note} />
          </VStack>
          <ValueProps
            vpTitle={paywall.vp_title}
            valueProps={paywall.value_props}
          />
          <Testimonials testimonials={paywall.testimonials} />
        </VStack>
        <StickyPurchaseButton
          isDisabled={waiting || !isPromoValid}
          loading={waiting}
          text={activeSubOffer.cta_text}
        />
      </Box>
    );
  },
);
